import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  HeartHandshake,
  UsersRound,
  UserCheck,
  Calendar,
  Store,
  MessageSquare,
  LayoutGrid,
  Settings,
  LogOut,
} from 'lucide-react';
import { supabase } from '../../services/supabase';
import { noLeidosDelBuzon } from '../../lib/buzonDelClub';
import { useAuthStore } from '../../store/authStore';

// Sidebar de 9 entradas (PR-UX2). Comunicaciones se promociona a item de primer
// nivel —con badge azul de mensajes sin leer— al salir del hub Gestión. El resto
// del grueso de páginas sigue dentro de dos hubs —Negocio (/negocio) y Gestión
// (/gestion)— que las presentan como tarjetas.
// (mig 151) La tecnificación individual se unificó en el motor de sesiones común
// (se crea desde "Grupos" tipo Individual, aforo 1). El modelo de "huecos" quedó
// obsoleto: su página /tecnificacion-individual sigue en el router pero SIN entrada
// de menú (limpieza en PR futuro).
const NAV = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/alumnos', icon: Users, label: 'Alumnos' },
  // Décima entrada, contra la regla de 9 de PR-UX2. Familias no es una página
  // más: es la única desde la que se ve quién accede a los datos de un menor, y
  // hay formas de familia que solo se pueden cerrar desde ahí. Enterrarla en el
  // hub Gestión —contenido, pedidos, analítica— la clasificaría como algo que
  // no es. Va pegada a Alumnos porque es la otra mitad de la misma pregunta.
  { to: '/familias', icon: HeartHandshake, label: 'Familias' },
  { to: '/monitores', icon: UserCheck, label: 'Entrenadores' },
  { to: '/sesiones', icon: UsersRound, label: 'Grupos' },
  { to: '/agenda', icon: Calendar, label: 'Sesiones' },
  { to: '/negocio', icon: Store, label: 'Negocio' },
  { to: '/comunicaciones', icon: MessageSquare, label: 'Comunicaciones' },
  { to: '/gestion', icon: LayoutGrid, label: 'Gestión' },
  { to: '/configuracion', icon: Settings, label: 'Configuración' },
];

export default function Sidebar() {
  const { reset, profile } = useAuthStore((s) => ({ reset: s.reset, profile: s.profile }));
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState<number | null>(0);
  /** El recuento, para quien lo necesite fuera del efecto que lo define. */
  const recontarRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!profile) return;
    let active = true;

    const fetchCount = async () => {
      // El buzón, no la persona: las familias escriben al admin, así que
      // preguntando por el propio id el globo de un director era siempre 0.
      const { noLeidos } = await noLeidosDelBuzon(profile.id);
      // ⚠️ EL `?? 0` SE VA: un 0 y un «no he podido contarlo» pintaban el mismo
      // hueco, y un globo que no está no se lee como «falló», se lee como «no
      // tienes nada». Ahora `null` se conserva y se pinta un `?`. Copiado de
      // `MonitorSidebar`, que ya lo distinguía: que la barra del ADMIN fuera la
      // única que no lo hacía es el argumento, no una opinión.
      //
      // Lo que esto NO explica, y conviene que quede dicho porque yo lo dije
      // mal: el «entras en Comunicaciones y el círculo desaparece» de Jose no
      // es este `null`. Es el efecto de la ruta, ahí abajo, que ponía un 0 a
      // mano sin haber leído nada. Los dos pintaban el mismo hueco por motivos
      // distintos, y por eso se arreglan los dos.
      if (active) setUnreadCount(noLeidos);
    };
    // Lo guardamos para que el efecto de la ruta pueda RECONTAR en vez de
    // inventarse un número. Un ref y no un `useCallback` para no tener que
    // meter la función en las dependencias del efecto que monta el canal
    // Realtime: ese canal se tumba si el efecto se re-ejecuta.
    recontarRef.current = fetchCount;
    fetchCount();

    // PR-T2.1.3 Bug 3: `messages` NO está en la publicación `supabase_realtime`
    // (solo `notifications`), así que la suscripción de abajo no dispara hoy y el
    // badge solo se refrescaba al montar. Refrescamos por polling + al recuperar
    // el foco de la pestaña para que el badge aparezca sin recargar. La
    // suscripción Realtime se mantiene: si algún día se publica `messages`,
    // dará refresco instantáneo además del polling.
    const interval = setInterval(fetchCount, 20000);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);

    const channel = supabase
      .channel(`sidebar-msgs-${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        fetchCount
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
        },
        fetchCount
      )
      .subscribe();

    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      supabase.removeChannel(channel);
    };
    // Dep deliberada `[profile?.id]`: solo el id determina la suscripción.
    // Listar `profile` entero re-ejecutaría el efecto en cada update del store
    // de auth (refresh de avatar, etc.) y tumbaría el canal Realtime.
    //
    // Y sin filtro `receiver_id`: el globo cuenta el buzón entero, así que un
    // mensaje dirigido a cualquiera del club tiene que refrescarlo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  // ⚠️ AQUÍ ESTABA EL «ENTRAS Y DESAPARECE» DE JOSE, y no en el `null` de
  // arriba. Este efecto hacía `setUnreadCount(0)` al navegar a Comunicaciones:
  // un cero **a mano**, sin haber leído nada ni haber contado nada. Entrabas y
  // el círculo se vaciaba; a los 20 s el poll recontaba y volvía el 4. El
  // círculo no decía «lo has leído», decía «he entrado en la pantalla».
  //
  // Y es exactamente el badge GUARDADO en vez de contado: la decisión de este
  // PR es que el número salga siempre de la misma consulta que pinta la lista,
  // así que entrar no lo pone a cero — lo RECUENTA. Con el marcado de lectura
  // que la pantalla hace al abrir un hilo, ese recuento baja de verdad, y si no
  // ha bajado es porque queda algo sin leer, que es lo que el círculo existe
  // para decir.
  useEffect(() => {
    if (location.pathname.includes('comunicaciones')) recontarRef.current();
  }, [location.pathname]);

  async function handleLogout() {
    await supabase.auth.signOut();
    reset();
  }

  return (
    <aside className="w-64 min-h-screen bg-bg-surface border-r border-bg-border flex flex-col fixed left-0 top-0 bottom-0 z-10">
      {/* Logo */}
      <div className="p-6 border-b border-bg-border">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="VERTEX Performance" className="h-12 w-auto" />
          <p className="text-gold-primary text-xs font-inter uppercase tracking-widest">Admin</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {NAV.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`sidebar-link-${to.slice(1)}`}
            className={({ isActive }) => (isActive ? 'sidebar-item-active' : 'sidebar-item')}
          >
            <Icon size={16} />
            <span className="flex-1">{label}</span>
            {/* NO SE HA PODIDO CONTAR ≠ NO HAY NADA. Los dos pintaban el mismo
                hueco, así que un fallo de recuento se leía como «todo visto».
                Mismo remedio, mismas clases y mismo `title` que
                `MonitorSidebar`, que era la única barra que ya lo distinguía. */}
            {to === '/comunicaciones' && unreadCount === null && (
              <span
                data-testid="sidebar-comunicaciones-sin-contar"
                className="min-w-[20px] h-5 rounded-full bg-error/20 text-error border border-error/40 text-xs font-bold flex items-center justify-center px-1"
                title="No se han podido contar los mensajes sin leer"
              >
                ?
              </span>
            )}
            {/* Badge azul de mensajes sin leer en la entrada Comunicaciones
                (PR-UX2, antes promocionado a /gestion). */}
            {to === '/comunicaciones' && (unreadCount ?? 0) > 0 && (
              <span
                data-testid="sidebar-comunicaciones-badge"
                className="min-w-[20px] h-5 rounded-full bg-[#2196F3] text-white text-xs font-bold flex items-center justify-center px-1"
              >
                {(unreadCount ?? 0) > 99 ? '99+' : unreadCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-bg-border">
        <button onClick={handleLogout} className="sidebar-item w-full">
          <LogOut size={16} />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </aside>
  );
}
